import { ArrowLeftOutlined, ArrowRightOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { Button, Form, Modal } from "antd";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../contexts/ToastContext";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import {
    createInsuranceSale,
    createInsuranceSaleCheckout,
    buildInsuranceSalesReturnUrl,
    enrichLocationsWithTeqCoverage,
    fetchTeqMobilityCoverageStates,
    getCompanyId,
    getLocationMongoId,
    getMissingInsuranceDetails,
    getDefaultInsuranceDateRange,
    isLocationTeqMobilityEnabled,
    isBookingAgentUrlConfigured,
    listInsuranceProducts,
    listLocations,
    listSellableCars,
    quoteInsuranceSale,
} from "../../lib/insuranceSaleApi";
import useInsuranceSaleSendPaymentLink from "../../hooks/useInsuranceSaleSendPaymentLink";
import { isSolaGateway } from "../../../../lib/sola/solaPaymentApi";
import "./sellInsuranceModal.css";
import useSolaCardPayment, { isSolaPaymentCancelled } from "../../../../components/payment/useSolaCardPayment";
import { fetchUserById } from "../../lib/teqMobilityCoverageApi";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";
import ReviewSaleStep from "./steps/ReviewSaleStep";
import SelectCustomerStep from "./steps/SelectCustomerStep";
import SelectProductStep from "./steps/SelectProductStep";
import SelectVehicleStep from "./steps/SelectVehicleStep";
import WizardProgress from "./shared/WizardProgress";

const STEP_FIELDS = [
    ["provider", "userId"],
    ["carId", "locationId", "dateRange"],
    ["insuranceId"],
];

const LAST_STEP = 3;

const buildReturnUrl = (reservationId, payment, process) =>
    buildInsuranceSalesReturnUrl(reservationId, payment, process);

const SellInsuranceModal = ({ open, onClose, onSuccess, availableProviders, initialCustomerId = null }) => {
    const { t } = useTranslation("sellInsurance");
    const toast = useToast();
    const { myCompanyData } = useUser();
    const { requestSolaCardPayment, solaCardModal } = useSolaCardPayment();
    const [form] = Form.useForm();

    const [step, setStep] = useState(0);
    const [customer, setCustomer] = useState(null);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [cars, setCars] = useState([]);
    const [carsLoading, setCarsLoading] = useState(false);
    const [locations, setLocations] = useState([]);
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [teqCoverageStates, setTeqCoverageStates] = useState([]);
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [quoteError, setQuoteError] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [createdReservationId, setCreatedReservationId] = useState(null);
    const clientRequestIdRef = useRef(null);
    const wasOpenRef = useRef(false);
    const { sendPaymentLink, loading: sendLinkLoading } = useInsuranceSaleSendPaymentLink();
    const paymentInProgress = submitLoading || sendLinkLoading;

    const provider = Form.useWatch("provider", form);
    const carId = Form.useWatch("carId", form);
    const locationId = Form.useWatch("locationId", form);
    const dateRange = Form.useWatch("dateRange", form);
    const insuranceId = Form.useWatch("insuranceId", form);

    const resetState = useCallback(() => {
        setStep(0);
        setCustomer(null);
        setCars([]);
        setLocations([]);
        setProducts([]);
        setQuote(null);
        setQuoteError(null);
        setSubmitLoading(false);
        setCreatedReservationId(null);
        clientRequestIdRef.current = null;
    }, []);

    useEffect(() => {
        // Reset only when the modal opens. The other dependencies (company data, toast,
        // translations) can change identity mid-flow, and resetting then wiped the
        // provider and customer the user had already picked.
        const justOpened = open && !wasOpenRef.current;
        wasOpenRef.current = open;

        if (!justOpened) {
            return;
        }

        resetState();
        form.resetFields();

        if (availableProviders.length === 1) {
            form.setFieldValue("provider", availableProviders[0]);
        }

        setLocationsLoading(true);
        Promise.all([listLocations(), fetchTeqMobilityCoverageStates()])
            .then(([locationList, coverageStates]) => {
                setTeqCoverageStates(coverageStates);
                setLocations(enrichLocationsWithTeqCoverage(locationList, coverageStates));
            })
            .catch((error) => {
                setLocations([]);
                setTeqCoverageStates([]);
                toast.error({
                    message: t("wizard.loadLocationsError"),
                    description: error?.response?.data?.message || error?.message,
                });
            })
            .finally(() => setLocationsLoading(false));
    }, [open, availableProviders, form, resetState, toast, t]);

    useEffect(() => {
        if (!open || !provider) {
            return;
        }

        form.setFieldsValue({ carId: undefined, insuranceId: undefined, dateRange: undefined });
        setQuote(null);
        setQuoteError(null);
        setProducts([]);
        setCars([]);
    }, [open, provider, form]);

    useEffect(() => {
        if (!open || !provider || !locationId) {
            return;
        }

        const [pickup, dropoff] = form.getFieldValue("dateRange") ?? [];
        if (pickup && dropoff) {
            return;
        }

        const selectedLocation = locations.find(
            (item) => getLocationMongoId(item) === locationId,
        );
        const isCovered =
            provider !== "teqmobility" ||
            isLocationTeqMobilityEnabled(selectedLocation, teqCoverageStates);

        if (!isCovered) {
            return;
        }

        form.setFieldsValue({ dateRange: getDefaultInsuranceDateRange() });
    }, [open, provider, locationId, locations, teqCoverageStates, form]);

    useEffect(() => {
        const [pickup, dropoff] = dateRange ?? [];

        if (!open || !provider || !locationId || !pickup || !dropoff) {
            setCars([]);
            return;
        }

        form.setFieldsValue({ carId: undefined, insuranceId: undefined });
        setQuote(null);
        setQuoteError(null);
        setProducts([]);

        setCarsLoading(true);
        listSellableCars(provider, locationId, pickup.toISOString(), dropoff.toISOString())
            .then(setCars)
            .catch((error) => {
                setCars([]);
                toast.error({
                    message: t("wizard.loadCarsError"),
                    description: error?.response?.data?.message || error?.message,
                });
            })
            .finally(() => setCarsLoading(false));
    }, [open, provider, locationId, dateRange, form, toast, t]);

    useEffect(() => {
        if (!open || !provider || !locationId || !carId) {
            setProducts([]);
            return;
        }

        const selectedLocation = locations.find(
            (item) => getLocationMongoId(item) === locationId,
        );
        const selectedCar = cars.find((car) => (car.id ?? car._id) === carId);

        if (
            provider === "teqmobility" &&
            !isLocationTeqMobilityEnabled(selectedLocation, teqCoverageStates)
        ) {
            form.setFieldValue("insuranceId", undefined);
            setQuote(null);
            setQuoteError(null);
            setProducts([]);
            return;
        }

        form.setFieldValue("insuranceId", undefined);
        setQuote(null);
        setQuoteError(null);

        setProductsLoading(true);
        listInsuranceProducts(provider, selectedCar?.worthValue)
            .then(setProducts)
            .catch((error) => {
                setProducts([]);
                toast.error({
                    message: t("wizard.loadProductsError"),
                    description: error?.response?.data?.message || error?.message,
                });
            })
            .finally(() => setProductsLoading(false));
    }, [open, provider, locationId, carId, cars, locations, teqCoverageStates, form, toast, t]);

    useEffect(() => {
        const [pickup, dropoff] = dateRange ?? [];
        let cancelled = false;

        if (!insuranceId || !carId || !locationId || !pickup || !dropoff) {
            setQuote(null);
            setQuoteError(null);
        } else {
            setQuoteLoading(true);
            setQuoteError(null);

            quoteInsuranceSale({
                insuranceId,
                carId,
                locationId,
                pickupDate: pickup.toISOString(),
                dropoffDate: dropoff.toISOString(),
                userId: form.getFieldValue("userId"),
            })
                .then((result) => {
                    if (!cancelled) {
                        setQuote(result);
                    }
                })
                .catch((error) => {
                    if (!cancelled) {
                        setQuote(null);
                        setQuoteError(error?.response?.data?.message || error?.message);
                    }
                })
                .finally(() => {
                    if (!cancelled) setQuoteLoading(false);
                });
        }

        return () => {
            cancelled = true;
        };
    }, [insuranceId, carId, locationId, dateRange, form]);

    const handleCustomerChange = async (userId) => {
        form.setFieldValue("userId", userId || undefined);

        if (!userId) {
            setCustomer(null);
            return;
        }

        setCustomerLoading(true);
        try {
            setCustomer(await fetchUserById(userId));
        } catch {
            setCustomer(null);
        } finally {
            setCustomerLoading(false);
        }
    };

    // Buying "for this customer": start the wizard with them already selected (runs after the reset above).
    useEffect(() => {
        if (open && initialCustomerId) {
            handleCustomerChange(initialCustomerId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialCustomerId]);

    const handleCancel = () => {
        resetState();
        form.resetFields();
        onClose();
    };

    const handleNext = async () => {
        try {
            await form.validateFields(STEP_FIELDS[step]);

            if (step === 0) {
                const missingDetails = getMissingInsuranceDetails(
                    customer,
                    provider,
                    myCompanyData,
                );
                if (missingDetails.length > 0) {
                    toast.error({
                        message: t("wizard.customerIncompleteTitle"),
                        description: t("wizard.customerIncompleteDescription", {
                            fields: missingDetails.join(", "),
                        }),
                    });
                    return;
                }
            }

            if (step === 2 && !quote) {
                toast.error({
                    message: t("wizard.quoteRequired"),
                    description: quoteError || undefined,
                });
                return;
            }

            setStep((prev) => prev + 1);
        } catch {
            // Validation errors are shown inline by the form.
        }
    };

    const createSaleRecord = async () => {
        const [pickup, dropoff] = dateRange ?? [];

        if (!quote || !pickup || !dropoff) {
            throw new Error(t("wizard.quoteRequired"));
        }

        const amountDue = Number(quote?.total ?? 0);

        if (createdReservationId) {
            return {
                reservationId: createdReservationId,
                amountDue,
            };
        }

        if (!clientRequestIdRef.current) {
            clientRequestIdRef.current =
                typeof crypto !== "undefined" && crypto.randomUUID
                    ? crypto.randomUUID()
                    : `sale-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }

        const response = await createInsuranceSale({
            userId: form.getFieldValue("userId"),
            carId,
            locationId,
            insuranceId,
            provider,
            pickupDate: pickup.toISOString(),
            dropoffDate: dropoff.toISOString(),
            clientRequestId: clientRequestIdRef.current,
        });

        if (response?.success === false) {
            throw new Error(response?.message || t("wizard.submitError"));
        }

        const reservationId = response?.data?.reservationId;
        if (!reservationId) {
            throw new Error(t("wizard.submitError"));
        }

        setCreatedReservationId(reservationId);

        return {
            reservationId,
            amountDue,
        };
    };

    const notifySaleCreatedUnpaid = (reservationId, error) => {
        toast.error({
            message: t("wizard.submitError"),
            description: t("wizard.saleCreatedUnpaid", {
                id: reservationId,
                reason: error?.response?.data?.message || error?.message,
            }),
        });
    };

    const handleSubmit = async () => {
        if (paymentInProgress) {
            return;
        }

        setSubmitLoading(true);
        try {
            const { reservationId, amountDue } = await createSaleRecord();
            const companyId = getCompanyId(myCompanyData);

            if (isSolaGateway(myCompanyData)) {
                try {
                    await requestSolaCardPayment({
                        reservationId,
                        reservationProcess: "insuranceSale",
                        amount: amountDue,
                        customerEmail: customer?.email,
                        companyId,
                    });
                } catch (solaError) {
                    if (!isSolaPaymentCancelled(solaError)) throw solaError;
                    window.location.href = buildReturnUrl(reservationId, "cancel", "insuranceSale");
                    return;
                }
                window.location.href = buildReturnUrl(reservationId, "success", "insuranceSale");
                return;
            }

            const checkoutResponse = await createInsuranceSaleCheckout({
                reservationId,
                amount: amountDue,
                customerEmail: customer?.email,
                companyId,
                reservationProcess: "insuranceSale",
                successUrl: buildReturnUrl(reservationId, "success", "insuranceSale"),
                cancelUrl: buildReturnUrl(reservationId, "cancel", "insuranceSale"),
            });

            if (checkoutResponse?.success === false) {
                throw new Error(checkoutResponse?.message || t("pay.stripeError"));
            }

            const paymentUrl = checkoutResponse?.data?.paymentUrl;
            if (!paymentUrl) {
                throw new Error(t("pay.missingPaymentUrl"));
            }

            window.location.href = paymentUrl;
        } catch (error) {
            if (createdReservationId) {
                notifySaleCreatedUnpaid(createdReservationId, error);
            } else {
                toast.error({
                    message: t("wizard.submitError"),
                    description: error?.response?.data?.message || error?.message,
                });
            }
            setSubmitLoading(false);
        }
    };

    const handleSendPaymentLink = async () => {
        if (paymentInProgress) {
            return;
        }

        setSubmitLoading(true);
        try {
            const { reservationId, amountDue } = await createSaleRecord();
            const sent = await sendPaymentLink({
                reservationId,
                amount: amountDue,
                customerEmail: customer?.email,
                reservationProcess: "insuranceSale",
            });

            if (sent) {
                onSuccess?.();
                handleCancel();
            }
        } catch (error) {
            if (createdReservationId) {
                notifySaleCreatedUnpaid(createdReservationId, error);
            } else {
                toast.error({
                    message: t("wizard.submitError"),
                    description: error?.response?.data?.message || error?.message,
                });
            }
        } finally {
            setSubmitLoading(false);
        }
    };

    const selectedCar = cars.find((car) => (car.id ?? car._id) === carId) ?? null;
    const selectedLocation =
        locations.find((item) => getLocationMongoId(item) === locationId) ?? null;
    const isLocationCoveredForProvider =
        provider !== "teqmobility" ||
        isLocationTeqMobilityEnabled(selectedLocation, teqCoverageStates);
    const [pickupDate, dropoffDate] = dateRange ?? [];
    const datesSelected = Boolean(pickupDate && dropoffDate);

    const stepItems = useMemo(
        () => [
            { title: t("wizard.stepCustomer") },
            { title: t("wizard.stepVehicle") },
            { title: t("wizard.stepProduct") },
            { title: t("wizard.stepReviewPay") },
        ],
        [t],
    );

    const footerButtonClass = "!h-11 !rounded-xl !px-5 !text-base !font-medium";

    const footerButtons = (
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <div className="flex gap-2">
                {step > 0 && step < LAST_STEP && (
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        className={`${footerButtonClass} flex-1 sm:flex-none`}
                        onClick={() => setStep((prev) => Math.max(0, prev - 1))}
                        disabled={paymentInProgress}>
                        {t("wizard.back")}
                    </Button>
                )}
                <Button
                    className={`${footerButtonClass} flex-1 sm:hidden`}
                    onClick={handleCancel}
                    disabled={paymentInProgress}>
                    {t("wizard.cancel")}
                </Button>
            </div>
            <div className="flex gap-2 sm:gap-3">
                <Button
                    className={`${footerButtonClass} hidden sm:inline-flex`}
                    onClick={handleCancel}
                    disabled={paymentInProgress}>
                    {t("wizard.cancel")}
                </Button>
                {step < LAST_STEP ? (
                    <Button
                        type="primary"
                        className={`${footerButtonClass} w-full sm:w-auto`}
                        onClick={handleNext}
                        loading={step === 2 && quoteLoading}>
                        {t("wizard.nextStep")}
                        <ArrowRightOutlined />
                    </Button>
                ) : null}
            </div>
        </div>
    );

    const modalTitle = (
        <div className="flex items-start gap-3 pr-8">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F0F0FE] text-lg text-[#5D5DF6]">
                <SafetyCertificateOutlined />
            </span>
            <div className="min-w-0">
                <p className="mb-0 text-lg font-semibold leading-tight text-gray-900 sm:text-[1.3rem]">
                    {t("wizard.title")}
                </p>
                <p className="mb-0 mt-0.5 text-sm font-normal text-gray-500 sm:text-base">{t("wizard.subtitle")}</p>
            </div>
        </div>
    );

    return (
        <>
            {solaCardModal}
        <Modal
            open={open}
            title={modalTitle}
            onCancel={handleCancel}
            footer={footerButtons}
            width={{ xs: "100vw", sm: "calc(100vw - 2rem)", lg: 1040 }}
            centered
            destroyOnClose
            maskClosable={false}
            rootClassName="sell-insurance-wizard-modal"
            classNames={{
                content: "sm:!max-w-[calc(100vw-2rem)] lg:!max-w-none",
            }}>
            <div className="flex-shrink-0 border-b border-gray-100 pb-3 pt-1 lg:pb-4">
                <WizardProgress
                    steps={stepItems}
                    current={step}
                    onStepClick={setStep}
                    disabled={paymentInProgress}
                />
            </div>

            <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto py-4 pr-0 sm:pr-1">
                <Form form={form} layout="vertical" requiredMark={customizeRequiredMark}>
                    <div style={{ display: step === 0 ? "block" : "none" }}>
                        <SelectCustomerStep
                            availableProviders={availableProviders}
                            provider={provider}
                            customer={customer}
                            customerLoading={customerLoading}
                            onCustomerChange={handleCustomerChange}
                            parentForm={form}
                            companyData={myCompanyData}
                        />
                    </div>
                    <div style={{ display: step === 1 ? "block" : "none" }}>
                        <SelectVehicleStep
                            provider={provider}
                            cars={cars}
                            carsLoading={carsLoading}
                            locations={locations}
                            locationsLoading={locationsLoading}
                            locationId={locationId}
                            datesSelected={datesSelected}
                            isLocationCoveredForProvider={isLocationCoveredForProvider}
                            showTeqCoverageIcons={provider === "teqmobility"}
                            selectedCarId={carId}
                            onCarSelect={(id) => form.setFieldValue("carId", id)}
                        />
                    </div>
                    <div style={{ display: step === 2 ? "block" : "none" }}>
                        <SelectProductStep
                            products={products}
                            productsLoading={productsLoading}
                            selectedProductId={insuranceId}
                            onSelectProduct={(id) => form.setFieldValue("insuranceId", id)}
                            quote={quote}
                            quoteLoading={quoteLoading}
                            quoteError={quoteError}
                            locationSelected={Boolean(locationId)}
                            carSelected={Boolean(carId)}
                            isLocationCoveredForProvider={isLocationCoveredForProvider}
                            carWorthValue={selectedCar?.worthValue}
                        />
                    </div>
                    {step === LAST_STEP && (
                        <ReviewSaleStep
                            provider={provider}
                            customer={customer}
                            car={selectedCar}
                            location={selectedLocation}
                            quote={quote}
                            stripeLoading={submitLoading}
                            sendLinkLoading={sendLinkLoading}
                            sendLinkDisabled={!isBookingAgentUrlConfigured()}
                            sendLinkDisabledReason={t("pay.bookingAgentRequired")}
                            onStripePay={handleSubmit}
                            onSendLink={handleSendPaymentLink}
                        />
                    )}
                </Form>
            </div>
        </Modal>
        </>
    );
};

SellInsuranceModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    availableProviders: PropTypes.arrayOf(PropTypes.string).isRequired,
    initialCustomerId: PropTypes.string,
};

SellInsuranceModal.defaultProps = {
    onSuccess: null,
};

export default SellInsuranceModal;
